import { Address } from 'shared/types/account';
import { Cart } from 'shared/types/cart/Cart';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';

type CheckDeliveryAddressResult = {
  deliveryStatus: boolean;
  deliveryErrorMsg?: string;
};

export const hasAddressChanged = (editedAddress: Address, formData: Address): boolean => {
  return (
    editedAddress.firstName !== formData.firstName ||
    editedAddress.lastName !== formData.lastName ||
    editedAddress.streetName !== formData.streetName ||
    editedAddress.additionalAddressInfo !== formData.additionalAddressInfo ||
    editedAddress.state !== formData.state ||
    editedAddress.city !== formData.city ||
    editedAddress.postalCode !== formData.postalCode ||
    editedAddress.phone !== formData.phone ||
    editedAddress.isDefaultBillingAddress !== (formData.isDefaultBillingAddress || formData.isDefaultShippingAddress)
  );
};

export const hasAddressFieldsChanged = (editedAddress: Address, formData: Address): boolean => {
  return (
    editedAddress.streetName !== formData.streetName ||
    editedAddress.additionalAddressInfo !== formData.additionalAddressInfo ||
    editedAddress.state !== formData.state ||
    editedAddress.city !== formData.city ||
    editedAddress.postalCode !== formData.postalCode
  );
};

export const getUpdatedAddressFields = (editedAddress: Address, formData: Address): { [key: string]: any } => {
  const updatedFields: { [key: string]: any } = {};

  if (editedAddress.streetName !== formData.streetName && formData.streetName) {
    updatedFields.streetName = formData.streetName;
  }
  if (editedAddress.additionalAddressInfo !== formData.additionalAddressInfo && formData.additionalAddressInfo) {
    updatedFields.additionalAddressInfo = formData.additionalAddressInfo;
  }
  if (editedAddress.state !== formData.state && formData.state) {
    updatedFields.state = formData.state;
  }
  if (editedAddress.city !== formData.city && formData.city) {
    updatedFields.city = formData.city;
  }
  if (editedAddress.postalCode !== formData.postalCode && formData.postalCode) {
    updatedFields.postalCode = formData.postalCode;
  }
  return updatedFields;
};

export const newAddressFormData = {
  addressId: '',
  firstName: '',
  lastName: '',
  streetName: '',
  additionalAddressInfo: '',
  state: '',
  city: '',
  postalCode: '',
  phone: '',
};

export const addressWatchedFields = ['streetName', 'additionalAddressInfo', 'state', 'city', 'postalCode'];

export const mapAddressAccountData = (accountData: any) => {
  const data = accountData?.data;
  let address;
  if (accountData?.data?.addresses) {
    address = accountData?.data?.addresses?.find((address: any) => address?.isDefaultShippingAddress === true);
  }
  return {
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    streetName: address?.streetName || '',
    additionalAddressInfo: address?.additionalAddressInfo || '',
    postalCode: address?.postalCode || '',
    city: address?.city || '',
    state: address?.state || '',
    country: address?.country || 'US',
    phone: address?.phone || '',
  };
};

export const compareAddresses = (formData: any, pickListSelectedAddress: any) => {
  if (!pickListSelectedAddress) {
    return true;
  }
  const fieldsToCompare = ['streetName', 'state', 'postalCode', 'city'];
  if (formData.additionalAddressInfo || pickListSelectedAddress.additionalAddressInfo) {
    fieldsToCompare.push('additionalAddressInfo');
  }
  for (const field of fieldsToCompare) {
    if (formData[field] !== pickListSelectedAddress[field]) {
      return true;
    }
  }
  // to be implemented when validate Address service is ready till then returing false.
  return false;
};

const checkAddressLinesForPOBox = (address: Address) => {
  const regex =
    /((.*p\[ \.\]\? \?(o|0)\[\-\. \/\\\]\? *\-?((box|bin)|num?\d+))|(p *\-?\/?(o)? *\-?box))|post office box/i;
  const { streetName, additionalAddressInfo } = address;
  return (streetName && regex.test(streetName)) || (additionalAddressInfo && regex.test(additionalAddressInfo));
};

export const checkDeliveryAddress = (address: Address, cart: Cart): CheckDeliveryAddressResult => {
  if (cart?.containsPOBoxItem && checkAddressLinesForPOBox(address)) {
    return {
      deliveryStatus: false,
      deliveryErrorMsg: mapLocaleToMeaningfulFormat().deliveryError,
    };
  }
  if (cart?.stateNotAvail && cart?.stateNotAvail?.length > 0 && address?.state) {
    if (cart?.stateNotAvail?.length > 0) {
      const stateFound = cart?.stateNotAvail?.find(
        (state) => state?.trim()?.toLowerCase() === address?.state?.trim()?.toLowerCase(),
      );
      if (stateFound) {
        return {
          deliveryStatus: false,
          deliveryErrorMsg: mapLocaleToMeaningfulFormat([`${address?.state}`]).stateNotAvailPageLevelText,
        };
      }
    }
  }
  return { deliveryStatus: true };
};

export const findMatchingAddress = (customerAccountAddresses: any[], data: any) => {
  return customerAccountAddresses.find(
    (address) =>
      address.firstName === data.firstName &&
      address.lastName === data.lastName &&
      address.streetName === data.streetName &&
      address.city === data.city &&
      address.state === data.state &&
      address.postalCode === data.postalCode &&
      address.phone === data.phone,
  );
};
